import { useState, useEffect, ChangeEvent, useMemo } from "react";
import { Search, Plus, Users } from "lucide-react";
import api from "../../services/api";
import AddCustomerModal from "../customers/AddCustomerModal";
import CustomerDetailsDrawer from "../customers/CustomerDetailsDrawer";
import PageHeader from "../ui/PageHeader";
import GlobalLoader from "../ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export type Customer = {
  id: number;
  name: string;
  email: string;
  mobile: string;
  mobile_with_code: string;
  formatted_mobile: string;
  calling_code: string;
  bookings: string;
  created: string;
  date_of_birth: string | null;
  image: string | null;
  user_image_url: string;
  customer_bookings: any[];
};

export default function CustomersList() {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Fetch customers from API (load all customers once)
  useEffect(() => {
    fetchCustomers();
  }, []); // Load all customers on mount

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all customers by loading all pages
      let allCustomers: any[] = [];
      let currentPageNum = 1;
      let hasMorePages = true;
      let totalCustomersCount = 0;
      let lastPageNum = 1;

      // Fetch all pages
      while (hasMorePages) {
        const response = await api.get(`/admin/customers?page=${currentPageNum}&per_page=1000`);
        const result = response.data;

        if (result.success && result.data) {
          // Get pagination info from first page
          if (currentPageNum === 1) {
            lastPageNum = result.data.last_page || 1;
            totalCustomersCount = result.data.total || 0;
          }

          // Extract customers from response
          let pageCustomers: any[] = [];
          if (result.data.data && Array.isArray(result.data.data)) {
            pageCustomers = result.data.data;
          } else if (Array.isArray(result.data)) {
            pageCustomers = result.data;
          }

          if (pageCustomers.length > 0) {
            allCustomers = allCustomers.concat(pageCustomers);
            console.log(`Fetched ${pageCustomers.length} customers from page ${currentPageNum}, total: ${allCustomers.length}`);
          }

          // Check if there are more pages
          hasMorePages = currentPageNum < lastPageNum;
          if (hasMorePages) {
            currentPageNum++;
          }
        } else {
          hasMorePages = false;
        }
      }

      // Map all customers to the expected format
      const apiCustomers = allCustomers.map((customer: any) => {
        // Count total bookings
        const totalBookings = customer.customer_bookings?.length || 0;

        return {
          id: customer.id,
          name: customer.name,
          email: customer.email,
          mobile: customer.mobile,
          mobile_with_code: customer.mobile_with_code,
          formatted_mobile: customer.formatted_mobile,
          calling_code: customer.calling_code,
          bookings: totalBookings.toString(),
          created: new Date(customer.created_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          date_of_birth: customer.date_of_birth,
          image: customer.image,
          user_image_url: customer.user_image_url,
          customer_bookings: customer.customer_bookings || [],
        };
      });

      setCustomers(apiCustomers);
      setSelectedCustomer((prev) =>
        prev
          ? apiCustomers.find((customer: Customer) => customer.id === prev.id) || prev
          : prev,
      );
      setTotalPages(1); // Client-side pagination, so only 1 page
      setTotal(totalCustomersCount || apiCustomers.length);
      console.log(`Loaded all ${apiCustomers.length} customers`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "An error occurred while fetching customers";
      setError(errorMessage);
      toast.error(errorMessage);
      console.error("Error fetching customers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustomer = (data: any) => {
    // Refresh the list after adding
    fetchCustomers();
  };

  // Filter customers
  const filtered = useMemo(() => {
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase()) ||
        c.mobile.includes(search)
    );
  }, [customers, search]);

  // Paginated customers
  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  }, [filtered, currentPage, itemsPerPage]);

  const totalPagesFiltered = Math.ceil(filtered.length / itemsPerPage);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const openDrawer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDrawerOpen(true);
  };

  return (
    <div className="">
      <div className="flex flex-col w-full h-full ">
        {/* Header */}
        <PageHeader
          title="Customers"
          description="View, add, edit and delete your client's details"
          icon={Users}
          variant="default"
          className="mb-0"
          actions={
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center justify-center bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg px-4 lg:px-6 py-2.5 lg:py-3 text-sm lg:text-[16px] font-semibold transition-colors w-full sm:w-auto shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden xs:inline">Add Customer</span>
              <span className="xs:hidden">Add</span>
            </button>
          }
        />

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 py-4 px-4 lg:px-0">
          {/* Left: Per Page Selector */}
          <div className="flex items-center gap-2 order-2 sm:order-1">
            <span className="text-xs lg:text-sm text-gray-500 font-medium">Show</span>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(value) => {
                setItemsPerPage(Number(value));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[70px] lg:w-[80px] bg-white border-gray-200 h-9 lg:h-10 text-xs lg:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs lg:text-sm text-gray-500 font-medium whitespace-nowrap">per page</span>
          </div>

          {/* Right: Search */}
          <div className="relative w-full sm:w-80 order-1 sm:order-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-xs lg:text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all h-9 lg:h-10"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 pb-6 px-4 lg:px-0">
          <div className="flex-1 rounded-xl lg:border border-gray-200 lg:shadow-sm bg-transparent lg:bg-white overflow-hidden flex flex-col">
            <div className="flex-1 overflow-y-auto relative no-scrollbar">
              <table className="w-full border-separate border-spacing-y-3 lg:border-spacing-y-0">
                <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
                  <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
                    <th className="col-span-4 text-left font-bold">Client Name</th>
                    <th className="col-span-3 text-left font-bold">Mobile Number</th>
                    <th className="col-span-2 text-left font-bold">Bookings</th>
                    <th className="col-span-3 text-left font-bold">Customer Since</th>
                  </tr>
                </thead>
                <tbody className="w-full lg:divide-y divide-gray-100 bg-transparent lg:bg-white">
                  {loading ? (
                    <tr className="h-64 w-full bg-white lg:bg-transparent rounded-xl">
                      <td colSpan={12} className="h-full py-12">
                        <GlobalLoader description="Loading customers..." compensateSidebar={false} />
                      </td>
                    </tr>
                  ) : error ? (
                    <tr className="h-64 w-full bg-white lg:bg-transparent rounded-xl">
                      <td colSpan={12} className="h-full py-12">
                        <div className="flex flex-col items-center justify-center text-center px-6">
                          <p className="text-lg font-semibold text-gray-900 mb-2">
                            Unable to load customers
                          </p>
                          <p className="text-gray-500 mb-6">{error}</p>
                          <button
                            onClick={fetchCustomers}
                            className="px-6 py-2 rounded-lg bg-[#885ABB] text-white font-medium hover:bg-[#6f47a2] transition-all"
                          >
                            Try Again
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedCustomers.length === 0 ? (
                    <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full bg-white lg:bg-transparent rounded-xl">
                      <td className="w-full h-full flex flex-col items-center justify-center col-span-12">
                        <Search className="w-12 h-12 mb-3 opacity-20" />
                        <p className="text-lg font-medium text-gray-500">
                          No customers found
                        </p>
                        <p className="text-sm">Try adjusting your search</p>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {paginatedCustomers.map((c) => (
                        <tr
                          key={c.id}
                          className="flex flex-col lg:grid lg:grid-cols-12 gap-2 lg:gap-4 items-start lg:items-center px-4 py-4 lg:px-6 hover:bg-purple-50/30 transition-colors bg-white lg:bg-white group text-sm w-full cursor-pointer rounded-xl lg:rounded-none shadow-sm lg:shadow-none border border-gray-100 lg:border-none"
                          onClick={() => openDrawer(c)}
                        >
                          <td className="lg:col-span-4 w-full">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 lg:h-10 lg:w-10 rounded-full bg-purple-100 flex items-center justify-center font-bold text-purple-600 overflow-hidden flex-shrink-0 shadow-inner">
                                {c.image || c.user_image_url ? (
                                  <img
                                    src={c.user_image_url}
                                    alt={c.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  c.name.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-800 lg:group-hover:text-[#885ABB] transition-colors truncate text-sm lg:text-base">
                                  {c.name}
                                </p>
                                <p className="text-[11px] lg:text-xs text-gray-500 truncate">{c.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="lg:col-span-3 text-gray-700 w-full flex lg:block justify-between items-center py-1 lg:py-0 border-t lg:border-none border-gray-50 mt-2 lg:mt-0 pt-2 lg:pt-0">
                            <span className="lg:hidden text-[10px] uppercase font-bold text-gray-400 tracking-wider">Mobile</span>
                            <span className="text-xs lg:text-sm font-medium lg:font-normal">{c.formatted_mobile || c.mobile || "—"}</span>
                          </td>
                          <td className="lg:col-span-2 text-gray-900 w-full flex lg:block justify-between items-center py-1 lg:py-0">
                            <span className="lg:hidden text-[10px] uppercase font-bold text-gray-400 tracking-wider">Bookings</span>
                            <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 font-semibold text-[10px] lg:text-xs">
                              {c.bookings} {parseInt(c.bookings) === 1 ? 'Visit' : 'Visits'}
                            </span>
                          </td>
                          <td className="lg:col-span-3 text-gray-700 w-full flex lg:block justify-between items-center py-1 lg:py-0">
                            <span className="lg:hidden text-[10px] uppercase font-bold text-gray-400 tracking-wider">Joined</span>
                            <span className="text-[11px] lg:text-sm font-medium lg:font-normal">{c.created}</span>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {!loading && !error && totalPagesFiltered > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 lg:px-6 py-4 mt-4 gap-4 bg-white lg:bg-white rounded-xl shadow-sm lg:shadow-none border lg:border-t border-gray-100 lg:border-gray-200">
              <div className="text-[11px] lg:text-sm text-gray-600 font-medium order-2 sm:order-1">
                Showing <span className="text-gray-900">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="text-gray-900">{Math.min(currentPage * itemsPerPage, filtered.length)}</span> of <span className="text-gray-900">{filtered.length}</span> customers
              </div>
              <div className="flex items-center gap-3 order-1 sm:order-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 lg:px-4 lg:py-2 border border-gray-300 rounded-lg text-xs lg:text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  Prev
                </button>
                <div className="bg-purple-50 px-3 py-1.5 rounded-lg text-purple-700 text-xs lg:text-sm font-bold min-w-[80px] text-center border border-purple-100">
                  {currentPage} / {totalPagesFiltered}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPagesFiltered, prev + 1))}
                  disabled={currentPage === totalPagesFiltered}
                  className="p-2 lg:px-4 lg:py-2 border border-gray-300 rounded-lg text-xs lg:text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Customer Modal */}
      <AddCustomerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveCustomer}
      />

      {/* Right-side Drawer */}
      <CustomerDetailsDrawer
        customer={selectedCustomer}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onRefresh={fetchCustomers}
        onCustomerUpdated={(updated) => {
          setSelectedCustomer((prev) =>
            prev && prev.id === updated.id
              ? {
                  ...prev,
                  ...updated,
                  mobile: updated.mobile || prev.mobile,
                  name: updated.name || prev.name,
                  email: updated.email || prev.email,
                  date_of_birth:
                    updated.date_of_birth ?? prev.date_of_birth,
                  user_image_url:
                    updated.user_image_url || prev.user_image_url,
                  image: updated.image ?? prev.image,
                }
              : prev,
          );
          setCustomers((list) =>
            list.map((c) =>
              c.id === updated.id
                ? {
                    ...c,
                    ...updated,
                    mobile: updated.mobile || c.mobile,
                    name: updated.name || c.name,
                    email: updated.email || c.email,
                    date_of_birth:
                      updated.date_of_birth ?? c.date_of_birth,
                    user_image_url:
                      updated.user_image_url || c.user_image_url,
                    image: updated.image ?? c.image,
                  }
                : c,
            ),
          );
        }}
      />
    </div>
  );
}
